import * as React from "react";
import styled from "styled-components";

// Super quick virtual scroller for date picker

// Items to render above and below the viewport
const GUTTER = 4;

type VirtualScrollEntryProps = {
    height: number
}
const VirtualScrollEntry = styled.div<VirtualScrollEntryProps>`
    position: absolute;
    height: ${props => props.height}px;
    width: 100%;
`;
type VirtualScrollWrapperProps = {
    innerHeight: number
    verticalMargin: number
}
const VirtualScrollWrapper = styled.div<VirtualScrollWrapperProps>`
    position: relative;
    height: 100%;

    &>div:nth-child(1) {
        overflow-y: scroll;
        overflow-x: hidden;
        height: 100%;

        /* Hide scrollbars */
        ::-webkit-scrollbar { display: none; }  /* Webkit */
        -ms-overflow-style: none;               /* IE and older Edge */
        scrollbar-width: none;                  /* Firefox */
        
        &>div {
            position: relative;
            height: ${props => props.innerHeight}px;
            margin: ${props => props.verticalMargin}px 0;
        }
    }

    &>div:nth-child(2) {
        position: absolute;
        top:0;
        right:2px;
        width:8px;
        height: 20px;
        border-radius: 4px; 
        background: transparent;
        transition: background ${props => props.theme.transitions.long};
        transition-delay: 500ms;
        z-index: ${props => props.theme.zIndex.tooltip};
    }

    &:hover>div:nth-child(2) {
        transition: background ${props => props.theme.transitions.short};
        background: ${props => props.theme.palette.scrollbar.thumbActive};
    }
`;
type VirtualScrollProps<T> = {
    items: Array<T>
    renderer: (item: T, idx: number)=>React.ReactNode
    itemHeight: number
    scrollTop?: number
    onScroll?: (scrollTop: number, idx: number) => void
    style?: React.CSSProperties
    center?: boolean
    gutter?: number
}
const VirtualScroll:React.FC<VirtualScrollProps<{}>> = (props) => {
    const {
        items, renderer, itemHeight, 
        scrollTop: scrollTopProp, 
        onScroll: onScrollProp, 
        style, center, gutter = GUTTER
    } = props;

    // Scrollbars and scroll behavior
    const scrollbarRef = React.useRef<HTMLDivElement>(null);
    const scrollMouseStart = React.useRef(0);
    const scrollTopStart = React.useRef(0);
    const autoScrolling = React.useRef<boolean>(false);
    const scrollTimeout = React.useRef<number>(0);

    // Containers
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const [viewportHeight, setViewportHeight] = React.useState(0);
    const verticalMargin = center ? viewportHeight/2 - itemHeight/2 : 0;
    const innerHeight = items.length * itemHeight;
    
    // What are we rendering...
    const itemCount = items.length;
    const [ scrollTop, setScrollTop ] = React.useState(scrollTopProp||0);
    const startIndex = Math.max(Math.floor(scrollTop / itemHeight) - gutter, 0);
    const endIndex = Math.min(itemCount - 1, Math.floor((scrollTop + viewportHeight) / itemHeight) + gutter);

    const onScroll = (e: React.UIEvent | Element) => {
        const scrollTop = ('currentTarget' in e) ? e.currentTarget.scrollTop : e.scrollTop;
        setScrollTop(scrollTop);

        // If we are scrolling automatically keep checking for it finishing
        if (autoScrolling.current) {
            clearTimeout(scrollTimeout.current);
            scrollTimeout.current = window.setTimeout(()=>{
                autoScrolling.current = false;
            }, 250);
        }    

        // Update scrollbar
        if (scrollbarRef.current) {
            const scrollbarHeight = Math.min(Math.max(viewportHeight * viewportHeight / innerHeight, 20), innerHeight);
            const top = (scrollTop / innerHeight) * (100 - (scrollbarHeight / viewportHeight * 100));
            scrollbarRef.current.style.height = scrollbarHeight + "px";
            scrollbarRef.current.style.top = top + "%";
        }

        // Update external scroll position if it is user initiated scroll
        onScrollProp && !autoScrolling.current && onScrollProp(scrollTop, Math.floor(scrollTop/itemHeight));
    }

    // Update position if active date changes
    React.useEffect(()=>{
        if (scrollTopProp === undefined) return;
        autoScrolling.current = true;
        wrapperRef.current?.scrollTo({
            top: scrollTopProp,
            behavior: "smooth"
        });
    }, [scrollTopProp]);

    // Scroll to current position on mount
    React.useEffect(()=>{
        wrapperRef.current?.scrollTo({top: scrollTop});
        return ()=>{ clearTimeout(scrollTimeout.current) };
    }, [viewportHeight]);

    // Setup scrollbar
    React.useEffect(()=>{
        const onScrollStart = (e: MouseEvent) => {
            e.preventDefault();
            scrollMouseStart.current = e.clientY,
            scrollTopStart.current = wrapperRef.current?.scrollTop || 0
            window.addEventListener("mousemove", onScrollMove);
            window.addEventListener("mouseup", onScrollEnd);
        }
        const onScrollMove = (e: MouseEvent) => {
            const mouseDistance = e.clientY - scrollMouseStart.current;
            const scrollbarHeight = Math.min(Math.max(viewportHeight * viewportHeight / innerHeight, 20), innerHeight);
            const scrollDistance = mouseDistance / (viewportHeight-scrollbarHeight) * innerHeight;
            const scrollTop = scrollTopStart.current;
            wrapperRef.current?.scrollTo({top: scrollTop + scrollDistance});
        }
        const onScrollEnd = (e: MouseEvent) => {
            window.removeEventListener("mousemove", onScrollMove);
            window.removeEventListener("mouseup", onScrollEnd);
        }
        const cleanup = (elem: HTMLDivElement|null) => {
            window.removeEventListener("mousemove", onScrollMove);
            window.removeEventListener("mouseup", onScrollEnd);
            elem?.removeEventListener("mousedown", onScrollStart);
        }
        
        // Setup handler for manual scrolling
        scrollbarRef.current &&
            scrollbarRef.current.addEventListener("mousedown", onScrollStart);
        
        // Do an initial scroll to set positions
        wrapperRef.current &&
            onScroll(wrapperRef.current);

        return () => cleanup(scrollbarRef.current);
    }, [viewportHeight, innerHeight]);

    // Get viewport dimensions
    React.useLayoutEffect(()=>{
        window.requestAnimationFrame(()=>{
            wrapperRef.current && setViewportHeight(wrapperRef.current.offsetHeight);
        });
    }, []);

    // The items to render
    const renderItems: Array<React.ReactNode> = [];
    for (let i = startIndex; i <= endIndex; i++) {
        renderItems.push(
            <VirtualScrollEntry 
                key={i} 
                height={itemHeight} 
                style={{ top: `${i * itemHeight}px` }}>
                {renderer(items[i]!, i)}
            </VirtualScrollEntry>
        );
    }

    return <VirtualScrollWrapper innerHeight={innerHeight} verticalMargin={verticalMargin} style={style}>
        <div ref={wrapperRef} onScroll={onScroll}>
            <div>{renderItems}</div>
        </div>
        <div ref={scrollbarRef} />
    </VirtualScrollWrapper>
}

export default VirtualScroll;
export { VirtualScroll, VirtualScrollProps };